import React, { createContext, useContext, ReactNode } from 'react'

// Centralized text state for AutobahnplakateVotingPhase1 and its children.
// Keep it local to this folder to avoid leaking concerns elsewhere.

export type AutobahnplakateTexts = {
  // UI State
  activeIndex: number
  selectedVote: string | null
  results: Array<{
    token: string
    count: number
  }>

  isAfterVoting: boolean
  /** True once client knows localStorage; matches SSR on first paint to avoid hydration mismatch. */
  hasVotedEffective: boolean
  /** Index of the Result slide for slideTo (depends on which slides are rendered). */
  resultSlideIndex: number

  // Intro
  introOverline: string
  introHeadline: string
  introHeadlineHighlightedWord: string
  introButtonLabel: string
  introButtonLabelVoted: string

  // Voting
  votingTopline: string
  votingHeadline: string
  votingHeadlineHighlightedWord: string
  votingItemLabel1: string
  votingItemLabel2: string
  votingItemLabel3: string
  votingItemLabel4: string
  votingItemLabel5: string
  votingButtonLabel: string

  // Message
  messageTopline: string
  messageHeadline: string
  messageHeadlineHighlightedWord: string
  messageCopy: string
  messageTextareaPlaceholder: string
  messageTextareaErrorMessage: string
  messageSkipButtonLabel: string
  messageButtonLabel: string

  // Result
  resultVotingHeadline: string
  resultVotingInfo: string
  resultTopline: string
  resultHeadline: string
  resultHeadlineSuccess: string
  resultEMailLabel: string
  resultEMailPlaceholder: string
  resultEMailErrorMessage: string
  resultEMailPrivacyInfo: RvG.Contentful.BasicRichTextType

  // Actions
  next: () => void
  prev: () => void
  slideTo: (index: number) => void
  setResults: (results: Array<{ token: string; count: number }>) => void
  setSelectedVote: (vote: string | null) => void

  // Data
  options: Array<{
    token: string
    label: string
  }>
}

const AutobahnplakateTextsContext = createContext<AutobahnplakateTexts | null>(
  null
)

type ProviderProps = {
  value: AutobahnplakateTexts
  children: ReactNode
}

export function AutobahnplakateTextsProvider({
  value,
  children,
}: ProviderProps): JSX.Element {
  return (
    <AutobahnplakateTextsContext.Provider value={value}>
      {children}
    </AutobahnplakateTextsContext.Provider>
  )
}

export function useAutobahnplakateTexts(): AutobahnplakateTexts {
  const ctx = useContext(AutobahnplakateTextsContext)
  if (!ctx) {
    throw new Error(
      'useAutobahnplakateTexts must be used within AutobahnplakateTextsProvider'
    )
  }
  return ctx
}
